package uz.mint.frontend.i18n

/**
 * Supported site languages.
 *
 * Each locale is a **separate statically-exported route** rather than a client-side toggle:
 * crawlers need to fetch `/`, `/uz/` and `/en/` and see fully-rendered copy in that language.
 * A runtime-only switcher would leave all three sharing one indexable URL.
 *
 * Russian is the default and owns the site root.
 */
enum class Locale(
    val code: String,
    val htmlLang: String,
    /** Short label for the language switcher. */
    val label: String,
    /** Route prefix; empty for the default locale. */
    val routePrefix: String,
    /** Endonym shown in the language menu — a language names itself in its own language. */
    val nativeName: String,
) {
    RU("ru", "ru", "RU", "", "Русский"),
    UZ("uz", "uz-Latn", "UZ", "/uz", "Oʻzbekcha"),
    EN("en", "en", "EN", "/en", "English"),
    ;

    /** Home route for this locale, always with a trailing slash so static export resolves it. */
    val homePath: String get() = if (routePrefix.isEmpty()) "/" else "$routePrefix/"

    /**
     * Full catalogue route for this locale — `/plans`, `/uz/plans`, `/en/plans`.
     *
     * A per-locale route for the same reason the home page has one: the listing is a real page
     * with its own title and its own indexable URL per language.
     *
     * **No trailing slash, unlike [homePath].** Kobweb registers this route as `/plans` and
     * static export writes `plans.html` — there is no `plans/index.html`, so `/plans/` resolves
     * to neither the file nor the route. Index pages are the ones that get the slash.
     */
    val plansPath: String get() = "$routePrefix/plans"

    /**
     * The legal documents, as addresses rather than overlays.
     *
     * They were only ever reachable by clicking the footer, which meant they had no URL: nothing
     * to index, nothing to link in a Payme or Click cabinet application, and nothing to send a
     * customer who asks for the terms. Same no-trailing-slash rule as [plansPath].
     */
    val faqPath: String get() = "$routePrefix/faq"
    val offerPath: String get() = "$routePrefix/offer"
    val privacyPath: String get() = "$routePrefix/privacy"

    /**
     * Signed-in routes. Same no-trailing-slash rule as [plansPath].
     *
     * These are exported like every other page, but they are not marketing surface: they render
     * a skeleton and then ask the backend who is looking. Nothing indexable ends up in the
     * export, which is fine — a login form has nothing to rank for.
     */
    val loginPath: String get() = "$routePrefix/login"
    val profilePath: String get() = "$routePrefix/profile"
    val esimsPath: String get() = "$routePrefix/esims"

    /**
     * One purchase in full — `/order?ref=M-AB12CD34`.
     *
     * The reference travels as a query parameter rather than as a path segment, for the same
     * reason a destination does on `/plans`: static export writes one file per route, and a route
     * with an id in the path has no file to write. A parameter keeps the page linkable and
     * bookmarkable, which is what a customer forwarding "where is my eSIM" needs.
     */
    val orderPath: String get() = "$routePrefix/order"

    fun orderPath(reference: String): String = "$orderPath?ref=$reference"

    companion object {
        val DEFAULT = RU
    }
}
